import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { getIntegrationServiceIdsForCategory, IntegrationCategory, IntegrationServiceId, isIntegrationDefaultHidden } from '../../common/integrations'
import { useCallback } from 'react'

export const useIntegrations = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isLoading } = useGetAccountQuery({ accountId })

  const isIntegrationAllowed = useCallback(
    (integration: IntegrationServiceId): boolean => {
      const defaultAllowed = !isIntegrationDefaultHidden(integration)
      const restrictions = account?.data?.enterpriseConfig.integrationsConfig.restrictions
      if (restrictions && (restrictions.allowedIntegrations?.length || restrictions.deniedIntegrations?.length)) {
        if (restrictions.allowedIntegrations?.length) {
          return restrictions.allowedIntegrations.includes(integration) && !restrictions.deniedIntegrations.includes(integration)
        }
        if (restrictions.deniedIntegrations.includes(integration)) {
          return false
        }
        return defaultAllowed
      }
      return defaultAllowed
    },
    [account],
  )

  const isIntegrationCategoryAllowed = useCallback(
    (integrationCategory: string): boolean => {
      return getIntegrationServiceIdsForCategory(integrationCategory as IntegrationCategory).some((serviceId) => isIntegrationAllowed(serviceId))
    },
    [isIntegrationAllowed],
  )
  const calendarIntegrationOptionsByIntegration: {
    [key in IntegrationServiceId]?: SelectOption
  } = {
    ...(isIntegrationAllowed(IntegrationServiceId.GOOGLE_CALENDAR) && {
      [IntegrationServiceId.GOOGLE_CALENDAR]: { id: IntegrationServiceId.GOOGLE_CALENDAR, label: 'Google Calendar' },
    }),
    ...(isIntegrationAllowed(IntegrationServiceId.OUTLOOK_CALENDAR) && {
      [IntegrationServiceId.OUTLOOK_CALENDAR]: { id: IntegrationServiceId.OUTLOOK_CALENDAR, label: 'Microsoft Outlook Calendar' },
    }),
    ...(isIntegrationAllowed(IntegrationServiceId.EXCHANGE_CALENDAR) && {
      [IntegrationServiceId.EXCHANGE_CALENDAR]: {
        id: IntegrationServiceId.EXCHANGE_CALENDAR,
        label: 'Exchange Calendar (On-Premise)',
      },
    }),
  }
  const calendarIntegrationOptions = Object.values(calendarIntegrationOptionsByIntegration)

  return {
    isIntegrationAllowed,
    isIntegrationCategoryAllowed,
    isLoading,
    calendarIntegrationOptions,
    calendarIntegrationOptionsByIntegration,
  }
}
